import { JSONCompatible } from '@direct-frontend/stdlib';

import { createContext, createElement, Fragment, ReactElement, ReactNode } from 'react';
import { LoggerNodeInfo, LoggerNodeInfoOverride } from './types';

export interface LoggerContext {
    /**
     * Хук для логирования продуктовых событий в компонентах.
     * @param nodeInfo Название компонента и дополнительные атрибуты.
     * @param overrideNodeInfo Переопределение названия и атрибутов (обычно через props.logNode).
     * @returns Функции для логирования.
     */
    useLogger: (nodeInfo: LoggerNodeInfo, overrideNodeInfo?: LoggerNodeInfoOverride) => LoggerValue;
}

/**
 * Контракт, который должен реализовать сервис для реализации логирования.
 */
export interface LoggerValue {
    /**
     * Провайдер контекста для всех вложенных компонентов, используется для создания иерархии в логах.
     * @param props Пропсы провайдера
     * @returns Компонент
     */
    LogProvider: (props: { children: ReactNode }) => ReactElement | null;
    /**
     * Функция для логирования кликов и других событий, вызываемых действиями пользователя.
     */
    logClick: LoggerClickHandler;
    /**
     * Функция для логирования технических событий, которые напрямую не связаны с действиями пользователя.
     */
    logTech: LoggerTechHandler;
    /**
     * Функция для логирования проскроллов.
     */
    logScroll: LoggerScrollHandler;
    /**
     * React-хук для логирования технических событий, которые напрямую не связаны с действиями пользователя.
     */
    useLogTech: UseLogTech;
}

type EventData<T extends JSONCompatible<T>> = Record<string, T>;

export type LoggerClickHandler = <T extends JSONCompatible<T>>(data?: EventData<T>) => void;
export type LoggerScrollHandler = <T extends JSONCompatible<T>>(data?: EventData<T>) => void;
export type LoggerTechHandler = <T extends JSONCompatible<T>>(type: string, data?: EventData<T>) => void;
export type UseLogTech = <T extends JSONCompatible<T>>(logTechParams: {
    /**
     * Тип события
     */
    type: string;
    /**
     * Данные события
     */
    data?: EventData<T>;
    /**
     * Условие для логирования. По-умолчанию true
     */
    if?: boolean;
    /**
     * Функция, которая вызывается после логирования.
     */
    onLog?: () => void;
}) => void;

const initialContext: LoggerValue = {
    LogProvider({ children }) {
        return createElement(Fragment, null, children);
    },
    logClick() {},
    logTech() {},
    logScroll() {},
    useLogTech() {},
};

export const LoggerReactContext = createContext<LoggerContext>({
    useLogger: () => initialContext,
});

export const LoggerProvider = LoggerReactContext.Provider;
